import { useEffect, useState } from "react";
import { Button, FormField, Input } from "@/design-system/components/ui";
import { Select, Textarea } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import { useAddCustomChecklistItem } from "@/hooks/deals/useDealDetail";

interface Props {
  open: boolean;
  dealId: string;
  onClose: () => void;
}

const CATEGORIES = ["Counterparties", "Documents", "Verification", "Other"];

export function AddIntakeChecklistItemModal({ open, dealId, onClose }: Props) {
  const toast = useToast();
  const addItem = useAddCustomChecklistItem(dealId);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [required, setRequired] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setTitle("");
      setDescription("");
      setCategory(CATEGORIES[0]);
      setRequired(true);
      setError(null);
    }
  }, [open]);

  async function submit() {
    if (title.trim().length < 3) {
      setError("Give the item a short title.");
      return;
    }
    setError(null);
    try {
      await addItem.mutateAsync({
        title: title.trim(),
        description: description.trim() || null,
        category,
        required,
      });
      toast.success("Checklist item added.");
      onClose();
    } catch (err) {
      setError(
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : err instanceof Error
            ? err.message
            : "Failed to add item.",
      );
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="Add custom checklist item"
      description="Custom items are deal-specific and audit-logged."
      footer={
        <>
          <Button variant="tertiary" onClick={onClose} disabled={addItem.isPending}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} loading={addItem.isPending}>
            Add item
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-3">
        <FormField label="Title" required>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Collect updated insurance certificate"
            autoFocus
          />
        </FormField>

        <FormField label="Description">
          <Textarea
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional detail."
          />
        </FormField>

        <div className="grid grid-cols-2 gap-space-3">
          <FormField label="Category">
            <Select value={category} onChange={(e) => setCategory(e.target.value)}>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField label="Required?">
            <label className="inline-flex items-center gap-space-2 mt-space-2 text-small text-onyx">
              <Input
                type="checkbox"
                className="!w-4 !h-4 !p-0"
                checked={required}
                onChange={(e) => setRequired(e.target.checked)}
              />
              Required for review completion
            </label>
          </FormField>
        </div>

        {error && (
          <p className="text-small text-status-error" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
